import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import { Col, Form, Row } from "react-bootstrap";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl, useStateContext } from "../../../contexts/ContextProvider";
import { Loader } from "../../../components";

const METRICS = [
  { key: "global", label: "ANGSURAN GLOBAL" },
  { key: "angsuran1Sampai6", label: "ANGSURAN 1 S/D 6" },
  { key: "angsuran7KeAtas", label: "ANGSURAN 7 KE ATAS" },
];
const metricFields = ["bhn", "byr", "ttp", "ttl", "ach"];

const emptyReport = () => ({ groups: [], total: {} });

const RekapPencapaianPerSurveyor = () => {
  const { user } = useContext(AuthContext);
  const { screenSize } = useStateContext();
  const tableRef = useRef(null);
  const today = new Date();
  const [dariTgl, setDariTgl] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [sampaiTgl, setSampaiTgl] = useState(
    new Date(today.getFullYear(), today.getMonth() + 1, 0),
  );
  const [surveyors, setSurveyors] = useState([]);
  const [selectedSurveyor, setSelectedSurveyor] = useState(null);
  const [report, setReport] = useState(emptyReport());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const textRight = { textAlign: screenSize >= 650 ? "right" : "left" };
  const surveyorOptions = useMemo(
    () => [
      { _id: "", kodeSurveyor: "", namaSurveyor: "SEMUA SURVEYOR" },
      ...surveyors,
    ],
    [surveyors],
  );

  useEffect(() => {
    const loadSurveyors = async () => {
      try {
        const response = await axios.post(`${tempUrl}/surveyorsAllCabang`, {
          id: user._id,
          token: user.token,
        });
        setSurveyors(response.data || []);
      } catch (requestError) {
        setError(requestError.response?.data?.message || requestError.message);
      }
    };
    loadSurveyors();
  }, [user]);

  const showReport = async () => {
    if (!dariTgl || !sampaiTgl) {
      setError("Dari tanggal dan sampai tanggal harus diisi.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/jualsForRekapPencapaianPerSurveyor`,
        {
          dariTgl,
          sampaiTgl,
          kodeSurveyor: selectedSurveyor?._id || null,
          id: user._id,
          token: user.token,
        },
      );
      setReport(response.data || emptyReport());
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message);
      setReport(emptyReport());
    } finally {
      setLoading(false);
    }
  };

  const metricValue = (metric, field) =>
    metric && metric[field] !== undefined ? metric[field] : 0;

  const reportRowValues = (row) =>
    METRICS.flatMap((metric) =>
      metricFields.map((field) => metricValue(row?.[metric.key], field)),
    );

  const reportBodyRows = () => {
    const rows = [];
    for (const group of report.groups || []) {
      group.rows.forEach((row, index) => {
        rows.push([
          index === 0
            ? `${group.kodeSurveyor || ""} - ${group.namaSurveyor}`
            : "",
          row.circle,
          ...reportRowValues(row),
        ]);
      });
      rows.push(["SUBTOTAL", "", ...reportRowValues(group.subtotal)]);
    }
    if (report.groups?.length) {
      rows.push(["TOTAL", "", ...reportRowValues(report.total)]);
    }
    return rows;
  };

  const exportExcel = () => {
    const headerTop = [
      "SURVEYOR",
      "CIRCLE",
      "ANGSURAN GLOBAL",
      "",
      "",
      "",
      "",
      "ANGSURAN 1 S/D 6",
      "",
      "",
      "",
      "",
      "ANGSURAN 7 KE ATAS",
      "",
      "",
      "",
      "",
    ];
    const headerBottom = [
      "",
      "",
      ...METRICS.flatMap(() =>
        metricFields.map((field) => field.toUpperCase()),
      ),
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([headerTop, headerBottom]);
    XLSX.utils.sheet_add_aoa(worksheet, reportBodyRows(), { origin: "A3" });
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
      { s: { r: 0, c: 2 }, e: { r: 0, c: 6 } },
      { s: { r: 0, c: 7 }, e: { r: 0, c: 11 } },
      { s: { r: 0, c: 12 }, e: { r: 0, c: 16 } },
    ];
    worksheet["!cols"] = [
      { wch: 28 },
      { wch: 9 },
      ...Array.from({ length: 15 }, () => ({ wch: 9 })),
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rekap Pencapaian");
    XLSX.writeFile(workbook, "rekapPencapaianPerSurveyor.xlsx");
  };

  const printPdf = () => {
    const doc = new jsPDF("landscape", "mm", "a4");
    const topHeader = [
      { content: "SURVEYOR", rowSpan: 2 },
      { content: "CIRCLE", rowSpan: 2 },
      { content: "ANGSURAN GLOBAL", colSpan: 5 },
      { content: "ANGSURAN 1 S/D 6", colSpan: 5 },
      { content: "ANGSURAN 7 KE ATAS", colSpan: 5 },
    ];
    const bottomHeader = metricFields
      .concat(metricFields, metricFields)
      .map((field) => field.toUpperCase());

    doc.setFontSize(12);
    doc.text("REKAP PENCAPAIAN ANGSURAN - PER SURVEYOR", 10, 10);
    doc.setFontSize(8);
    doc.text(
      `Periode: ${dariTgl.toLocaleDateString("id-ID")} s/d ${sampaiTgl.toLocaleDateString("id-ID")}`,
      10,
      16,
    );
    doc.autoTable({
      startY: 21,
      margin: { left: 5, right: 5 },
      head: [topHeader, bottomHeader],
      body: reportBodyRows(),
      theme: "grid",
      tableWidth: "auto",
      styles: {
        fontSize: 5.5,
        cellPadding: 1,
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [33, 115, 190],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 38, halign: "left" },
        1: { cellWidth: 12 },
      },
      didParseCell: (data) => {
        if (
          data.section === "body" &&
          ["SUBTOTAL", "TOTAL"].includes(data.row.raw[0])
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [235, 240, 246];
        }
      },
    });
    window.open(URL.createObjectURL(doc.output("blob")));
  };

  if (loading) return <Loader persenLoading="50" />;

  return (
    <Box>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #rekap-pencapaian-print, #rekap-pencapaian-print * { visibility: visible; }
          #rekap-pencapaian-print { position: absolute; left: 0; top: 0; width: 100%; }
        }
        #rekap-pencapaian-print table { border-collapse: separate; border-spacing: 0; }
        #rekap-pencapaian-print th, #rekap-pencapaian-print td {
          border-right: 1px solid #d7e0ea;
          border-bottom: 1px solid #d7e0ea;
          padding: 7px 8px;
          text-align: center;
          white-space: nowrap;
        }
        #rekap-pencapaian-print th {
          background: #2173be;
          color: #fff;
          font-weight: 700;
          position: sticky;
          top: 0;
          z-index: 2;
        }
        #rekap-pencapaian-print th:first-child { border-left: 1px solid #d7e0ea; text-align: left; }
        #rekap-pencapaian-print td:first-child { border-left: 1px solid #d7e0ea; }
        #rekap-pencapaian-print .surveyor-cell { text-align: left; }
        #rekap-pencapaian-print tbody tr:nth-child(even):not(:last-child) { background: #f8fbff; }
        #rekap-pencapaian-print tbody tr:hover { background: #e9f3ff; }
      `}</style>
      <Typography color="#757575">Laporan</Typography>
      <Typography variant="h4" sx={{ fontWeight: 900 }}>
        Rekap Pencapaian / Surveyor
      </Typography>
      <Divider sx={{ margin: "20px 0" }} />
      <Box sx={{ marginBottom: 2 }}>
        <Row>
          <Col sm={6}>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm="4" style={textRight}>
                Surveyor :
              </Form.Label>
              <Col sm="8">
                <Autocomplete
                  size="small"
                  options={surveyorOptions}
                  value={selectedSurveyor}
                  onChange={(_, value) =>
                    setSelectedSurveyor(value?._id ? value : null)
                  }
                  getOptionLabel={(option) =>
                    option.namaSurveyor === "SEMUA SURVEYOR"
                      ? option.namaSurveyor
                      : `${option.kodeSurveyor || ""} - ${option.namaSurveyor || ""}`
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="SEMUA SURVEYOR" />
                  )}
                />
              </Col>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm="4" style={textRight}>
                Dari Tanggal :
              </Form.Label>
              <Col sm="8">
                <DatePicker
                  dateFormat="dd/MM/yyyy"
                  selected={dariTgl}
                  onChange={setDariTgl}
                  customInput={<TextField size="small" fullWidth />}
                />
              </Col>
            </Form.Group>
          </Col>
          <Col sm={6}></Col>
          <Col sm={6}>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm="4" style={textRight}>
                Sampai Tanggal :
              </Form.Label>
              <Col sm="8">
                <DatePicker
                  dateFormat="dd/MM/yyyy"
                  selected={sampaiTgl}
                  onChange={setSampaiTgl}
                  customInput={<TextField size="small" fullWidth />}
                />
              </Col>
            </Form.Group>
          </Col>
          <Col sm={6}></Col>
        </Row>
        {error && <Typography color="error">{error}</Typography>}
        <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={showReport}
          >
            TAMPILKAN
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            disabled={!report.groups?.length}
            onClick={printPdf}
          >
            CETAK
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={!report.groups?.length}
            onClick={exportExcel}
          >
            EXCEL
          </Button>
        </Box>
      </Box>

      <Box
        id="rekap-pencapaian-print"
        ref={tableRef}
        sx={{ overflowX: "auto" }}
      >
        <Typography sx={{ fontWeight: 700, marginBottom: 1 }}>
          Rekap Pencapaian Angsuran - Per Surveyor
        </Typography>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th rowSpan="2">SURVEYOR</th>
              <th rowSpan="2">CIRCLE</th>
              {METRICS.map((metric) => (
                <th key={metric.key} colSpan="5">
                  {metric.label}
                </th>
              ))}
            </tr>
            <tr>
              {METRICS.flatMap((metric) =>
                metricFields.map((field) => (
                  <th key={`${metric.key}-${field}`}>{field.toUpperCase()}</th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {(report.groups || []).map((group) => (
              <React.Fragment key={group.surveyorId}>
                {group.rows.map((row, index) => (
                  <tr key={`${group.surveyorId}-${row.circle}`}>
                    {index === 0 && (
                      <td
                        rowSpan={group.rows.length}
                        className="surveyor-cell"
                        style={surveyorCellStyle}
                      >
                        {`${group.kodeSurveyor || ""} - ${group.namaSurveyor}`}
                      </td>
                    )}
                    <td>{row.circle}</td>
                    {METRICS.flatMap((metric) =>
                      metricFields.map((field) => (
                        <td key={`${row.circle}-${metric.key}-${field}`}>
                          {metricValue(row[metric.key], field)}
                        </td>
                      )),
                    )}
                  </tr>
                ))}
                <SummaryRow
                  label="SUBTOTAL"
                  metrics={group.subtotal}
                  metricValue={metricValue}
                />
              </React.Fragment>
            ))}
            {!!report.groups?.length && (
              <SummaryRow
                label="TOTAL"
                metrics={report.total}
                metricValue={metricValue}
              />
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

const SummaryRow = ({ label, metrics, metricValue }) => (
  <tr style={{ fontWeight: 700, background: "#f3f3f3" }}>
    <td colSpan="2">{label}</td>
    {METRICS.flatMap((metric) =>
      metricFields.map((field) => (
        <td key={`${label}-${metric.key}-${field}`}>
          {metricValue(metrics?.[metric.key], field)}
        </td>
      )),
    )}
  </tr>
);

const tableStyle = {
  borderCollapse: "collapse",
  width: "100%",
  minWidth: "1250px",
  fontSize: "0.78rem",
};

const surveyorCellStyle = {
  fontWeight: 700,
  color: "#174f80",
  verticalAlign: "top",
  background: "#eef6ff",
};

export default RekapPencapaianPerSurveyor;
